import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Home, Users, Wallet, Sparkles } from "lucide-react";

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);

function useCountUp(target, ms = 1200) {
  const [n, setN] = useState(0);
  const raf = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (target - from) * eased));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, ms]);
  return n;
}

function Card({ icon, label, value, format = (v) => v.toLocaleString(), testid }) {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { rootMargin: "-10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const n = useCountUp(inView ? value : 0);
  return (
    <div ref={ref} className="mv-card p-6" data-testid={testid}>
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text mb-3"
           style={{ background: "rgba(212,175,55,0.16)" }}>{icon}</div>
      <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>{label}</div>
      <div className="font-display text-4xl mv-green-text mt-1">{format(n)}</div>
    </div>
  );
}

export default function ImpactLiveCounter() {
  const [totals, setTotals] = useState({ homes: 0, families: 0, fees: 0, jobs: 0 });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/impact-reports");
        const list = data.reports || [];
        const t = list.reduce((a, r) => ({
          homes:    a.homes    + (r.homes_delivered || 0),
          families: a.families + (r.families_helped || 0),
          fees:     a.fees     + (r.fees_saved || 0),
          jobs:     a.jobs     + (r.jobs_created || 0),
        }), { homes: 0, families: 0, fees: 0, jobs: 0 });
        setTotals(t);
      } catch {}
      setLoaded(true);
    })();
  }, []);

  const anyData = totals.homes || totals.families || totals.fees || totals.jobs;
  if (!loaded || !anyData) return null;

  return (
    <section className="py-14 md:py-18 border-t mv-hairline" data-testid="impact-live-counter"
             style={{ background: "linear-gradient(180deg, #fff, rgba(10,58,36,0.04))" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Live impact — real numbers</span></div>
          <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
            Not promises. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Delivered.</span>
          </h2>
          <p className="mt-3 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
            Refreshed from our public quarterly reports.
          </p>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card icon={<Home size={22}/>}     label="Homes delivered"     value={totals.homes}    testid="ic-homes"/>
          <Card icon={<Users size={22}/>}    label="Families helped"     value={totals.families} testid="ic-families"/>
          <Card icon={<Sparkles size={22}/>} label="Local jobs created"  value={totals.jobs}     testid="ic-jobs"/>
          <Card icon={<Wallet size={22}/>}   label="Fees saved for members" value={totals.fees} format={money} testid="ic-fees"/>
        </div>
      </div>
    </section>
  );
}
